import axios, { AxiosRequestConfig, AxiosResponse, AxiosHeaders } from 'axios';
import {
    getAccessToken,
    getRefreshToken,
    setAccessToken,
    setRefreshToken,
    clearAll,
    getDecodedAccessToken,
} from './tokenManager';

export interface ApiRequestConfig extends AxiosRequestConfig {
    requiresAuth?: boolean;
    _retry?: boolean;
}

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000,
});

const normalizeHeaders = (rawHeaders: any): AxiosHeaders => {
    const safeHeaders: Record<string, string> = {};
    Object.entries(rawHeaders || {}).forEach(([key, value]) => {
        if (typeof value === 'string') {
            safeHeaders[key] = value;
        }
    });
    return AxiosHeaders.from(safeHeaders);
};

api.interceptors.request.use(
    (config) => {
        if (!config.headers || !(config.headers instanceof AxiosHeaders)) {
            config.headers = normalizeHeaders(config.headers);
        }

        const customConfig = config as ApiRequestConfig;
        if (customConfig.requiresAuth !== false) {
            const token = getAccessToken();
            if (token) {
                config.headers.set('Authorization', `Bearer ${token}`);
                // const decoded = getDecodedAccessToken();
                // const subCompanyId = decoded?.subCompanyId;
                // if (subCompanyId) {
                //   config.headers.set('X-Sub-Company-ID', subCompanyId);
                // }
            }
        }

        return config;
    },
    (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) prom.reject(error);
        else prom.resolve(token);
    });
    failedQueue = [];
};

api.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
        const originalRequest = error.config as ApiRequestConfig;

        if (
            error.response?.status === 401 &&
            !originalRequest._retry &&
            originalRequest.requiresAuth !== false
        ) {

            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token: string) => {
                            if (!originalRequest.headers || typeof originalRequest.headers.set !== 'function') {
                                originalRequest.headers = {
                                    ...(originalRequest.headers || {}),
                                    Authorization: `Bearer ${token}`,
                                };
                            } else {
                                originalRequest.headers.set('Authorization', `Bearer ${token}`);
                            }
                            resolve(api(originalRequest));
                        },
                        reject: (err: any) => reject(err),
                    });
                });
            }

            isRefreshing = true;

            try {
                const refreshToken = getRefreshToken();
                if (!refreshToken) throw new Error('No refresh token found');

                const res = await axios.post(`${BASE_URL}/api/auth/refresh-token`, {
                    refreshToken,
                });

                const { accessToken, refreshToken: newRefreshToken } = res.data.data;

                setAccessToken(accessToken);
                setRefreshToken(newRefreshToken);

                api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

                processQueue(null, accessToken);

                if (!originalRequest.headers || typeof originalRequest.headers.set !== 'function') {
                    originalRequest.headers = {
                        ...(originalRequest.headers || {}),
                        Authorization: `Bearer ${accessToken}`,
                    };
                } else {
                    originalRequest.headers.set('Authorization', `Bearer ${accessToken}`);
                }

                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                clearAll();
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export const getRequest = async <T>(url: string, config?: ApiRequestConfig): Promise<T> => {
    const token = getAccessToken();
    const headers = {
        ...(config?.headers || {}),
        Authorization: `Bearer ${token}`,
    };
    const response = await api.get<T>(url, { ...config, headers });
    return response.data;
};

export const postRequest = async <T>(url: string, data?: any, config?: ApiRequestConfig): Promise<T> => {
    const token = getAccessToken();
    const headers = {
        ...(config?.headers || {}),
        Authorization: `Bearer ${token}`,
    };
    const response = await api.post<T>(url, data, { ...config, headers });
    return response.data;
};

export const putRequest = async <T>(url: string, data?: any, config?: ApiRequestConfig): Promise<T> => {
    const token = getAccessToken();
    const headers = {
        ...(config?.headers || {}),
        Authorization: `Bearer ${token}`,
    };
    const response = await api.put<T>(url, data, { ...config, headers });
    return response.data;
};

export const deleteRequest = async <T>(url: string, config?: ApiRequestConfig): Promise<T> => {
    const token = getAccessToken();
    const headers = {
        ...(config?.headers || {}),
        Authorization: `Bearer ${token}`,
    };
    const response = await api.delete<T>(url, { ...config, headers });
    return response.data;
};

const apiClient = {
    get: getRequest,
    post: postRequest,
    put: putRequest,
    delete: deleteRequest,
};

export default apiClient;
